/**
 * The pegboard: a grid of studs that parts snap onto.
 *
 * Stacking rules (like a real snap kit):
 *  - Parts sit on the lowest layer where all the studs they cover are free.
 *  - A part's snaps (pins) may only rest on the board, on another part's snap, or on a little spacer
 *    (auto-added when a snap hangs over an empty stud). A snap can never rest on a part's plastic body.
 *  - Electrically, every snap at the same (x, y) is connected, whatever its layer.
 */
import { partDef } from '../parts/registry';
import { DIRS, cellKey, type Cell, type PartProps, type PartType, type Placement, type Rot } from './types';

export const MAX_LAYER = 5;
/** Elastic boards grow to keep this many free studs around the build. */
export const GROW_MARGIN = 2;
/** How far past the edge you can drop a part on an elastic board. */
export const GROW_REACH = 6;
export const MIN_SIZE = { cols: 8, rows: 6 };
export const MAX_SIZE = { cols: 30, rows: 22 };

export interface FootCell extends Cell {
  /** Index along the part (0 … length-1). */
  index: number;
  /** Pin number if this stud is a snap, else -1. */
  pin: number;
}

export type PlaceProblem = 'outside' | 'snap-on-body' | 'too-high';

export interface PlaceCheck {
  ok: boolean;
  layer: number;
  problem?: PlaceProblem;
}

export interface PlacementInput {
  type: PartType;
  x: number;
  y: number;
  rot: Rot;
  props: PartProps;
}

export function footprint(p: PlacementInput): FootCell[] {
  const def = partDef(p.type);
  if (def.shape) {
    // Free-form parts (jumper cables) list their own studs.
    return def.shape(p.props).map((c, i) => ({ x: p.x + c.dx, y: p.y + c.dy, index: i, pin: c.pin }));
  }
  const len = def.length(p.props);
  const pins = def.pins(p.props);
  const d = DIRS[p.rot]!;
  const out: FootCell[] = [];
  for (let i = 0; i < len; i++)
    out.push({ x: p.x + d.x * i, y: p.y + d.y * i, index: i, pin: pins.indexOf(i) });
  return out;
}

export const pinCells = (p: PlacementInput): FootCell[] => footprint(p).filter((c) => c.pin >= 0);

/** Rotate a part 180° in place: same studs, ends swapped (flips polarity). */
export function flipped(p: PlacementInput): { x: number; y: number; rot: Rot } {
  const cells = footprint(p);
  const end = cells[cells.length - 1]!;
  return { x: end.x, y: end.y, rot: ((p.rot + 2) % 4) as Rot };
}

/** Rotate 90° clockwise around the part's middle stud (kept on the grid). */
export function rotated(p: PlacementInput): { x: number; y: number; rot: Rot } {
  const cells = footprint(p);
  const mid = cells[Math.floor((cells.length - 1) / 2)]!;
  const rot = ((p.rot + 1) % 4) as Rot;
  const d = DIRS[rot]!;
  const back = Math.floor((cells.length - 1) / 2);
  return { x: mid.x - d.x * back, y: mid.y - d.y * back, rot };
}

export class Board {
  readonly parts = new Map<string, Placement>();
  /** Bumps on every change that affects wiring. The simulation rebuilds its netlist when it changes. */
  version = 0;
  private nextId = 1;

  /** Elastic boards (Free Build) grow as you build instead of having hard edges. */
  elastic = false;
  /**
   * Total distance parts have been shifted by growing. The renderer subtracts it so the build
   * never jumps on screen when the board grows to the left or top.
   */
  ox = 0;
  oy = 0;

  constructor(
    public cols = 10,
    public rows = 8,
  ) {}

  newId(): string {
    while (this.parts.has(`p${this.nextId}`)) this.nextId++;
    return `p${this.nextId++}`;
  }

  inBounds(c: Cell): boolean {
    return c.x >= 0 && c.y >= 0 && c.x < this.cols && c.y < this.rows;
  }

  /** Can a part reach this stud? On elastic boards, a little past the edge is fine: the board will grow. */
  reachable(c: Cell): boolean {
    if (!this.elastic) return this.inBounds(c);
    const r = GROW_REACH;
    return c.x >= -r && c.y >= -r && c.x < this.cols + r && c.y < this.rows + r;
  }

  /**
   * Grow an elastic board so every part has GROW_MARGIN free studs around it.
   * Never shrinks (so nothing jumps around while you build). Returns how far parts moved.
   */
  grow(): { dx: number; dy: number } {
    if (!this.elastic) return { dx: 0, dy: 0 };
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of this.parts.values())
      for (const c of footprint(p)) {
        minX = Math.min(minX, c.x);
        minY = Math.min(minY, c.y);
        maxX = Math.max(maxX, c.x);
        maxY = Math.max(maxY, c.y);
      }
    if (minX === Infinity) {
      // Empty again: back to a cozy starting size.
      if (this.cols === 10 && this.rows === 7) return { dx: 0, dy: 0 };
      this.cols = 10;
      this.rows = 7;
      this.version++;
      return { dx: 0, dy: 0 };
    }
    const m = GROW_MARGIN;
    const dx = Math.max(0, m - minX);
    const dy = Math.max(0, m - minY);
    const cols = Math.min(MAX_SIZE.cols, Math.max(this.cols + dx, maxX + dx + 1 + m, MIN_SIZE.cols));
    const rows = Math.min(MAX_SIZE.rows, Math.max(this.rows + dy, maxY + dy + 1 + m, MIN_SIZE.rows));
    if (!dx && !dy && cols === this.cols && rows === this.rows) return { dx: 0, dy: 0 };
    for (const p of this.parts.values()) {
      p.x += dx;
      p.y += dy;
    }
    this.ox += dx;
    this.oy += dy;
    this.cols = cols;
    this.rows = rows;
    this.version++;
    return { dx, dy };
  }

  /** Everything stacked on a stud, bottom first. */
  stackAt(
    x: number,
    y: number,
    exclude?: string,
    belowLayer = Infinity,
  ): { part: Placement; cell: FootCell }[] {
    const out: { part: Placement; cell: FootCell }[] = [];
    for (const part of this.parts.values()) {
      if (part.id === exclude || part.layer >= belowLayer) continue;
      for (const cell of footprint(part)) if (cell.x === x && cell.y === y) out.push({ part, cell });
    }
    return out.sort((a, b) => a.part.layer - b.part.layer);
  }

  /**
   * Where (and whether) a part could go. `exclude` = the part being moved.
   * `belowLayer` only considers parts beneath that layer (used when settling).
   */
  check(p: PlacementInput, exclude?: string, belowLayer = Infinity): PlaceCheck {
    const cells = footprint(p);
    if (!cells.every((c) => this.reachable(c))) return { ok: false, layer: 0, problem: 'outside' };
    if (this.elastic) {
      const xs = cells.map((c) => c.x);
      const ys = cells.map((c) => c.y);
      const w = Math.max(this.cols, ...xs.map((x) => x + 1)) - Math.min(0, ...xs);
      const h = Math.max(this.rows, ...ys.map((y) => y + 1)) - Math.min(0, ...ys);
      if (w > MAX_SIZE.cols || h > MAX_SIZE.rows) return { ok: false, layer: 0, problem: 'outside' };
    }
    let layer = 0;
    for (const c of cells) {
      const stack = this.stackAt(c.x, c.y, exclude, belowLayer);
      if (stack.length) layer = Math.max(layer, stack[stack.length - 1]!.part.layer + 1);
    }
    if (layer > MAX_LAYER) return { ok: false, layer, problem: 'too-high' };
    for (const c of cells) {
      if (c.pin < 0) continue;
      const stack = this.stackAt(c.x, c.y, exclude, belowLayer);
      const top = stack[stack.length - 1];
      if (top && top.cell.pin < 0) return { ok: false, layer, problem: 'snap-on-body' };
    }
    return { ok: true, layer };
  }

  /** Low-level insert (commands validate first). */
  insert(p: Placement): void {
    this.parts.set(p.id, { ...p, props: { ...p.props } });
    this.version++;
  }

  delete(id: string): Placement | undefined {
    const p = this.parts.get(id);
    if (!p) return undefined;
    this.parts.delete(id);
    this.settle();
    this.version++;
    return p;
  }

  /** After something is removed or moved, let parts above drop down where they can. */
  settle(): void {
    const order = [...this.parts.values()].sort((a, b) => a.layer - b.layer);
    for (const p of order) {
      if (p.layer === 0) continue;
      const chk = this.check(p, p.id, p.layer);
      if (chk.ok && chk.layer < p.layer) p.layer = chk.layer;
    }
  }

  /** Which parts are under any of the studs a part covers (they hold it up). */
  supportsOf(id: string): string[] {
    const p = this.parts.get(id);
    if (!p) return [];
    const ids = new Set<string>();
    for (const c of footprint(p))
      for (const s of this.stackAt(c.x, c.y, id)) if (s.part.layer < p.layer) ids.add(s.part.id);
    return [...ids];
  }

  /** Parts resting on top of `id`. */
  stackedOn(id: string): string[] {
    const p = this.parts.get(id);
    if (!p) return [];
    const ids = new Set<string>();
    for (const c of footprint(p))
      for (const s of this.stackAt(c.x, c.y, id)) if (s.part.layer > p.layer) ids.add(s.part.id);
    return [...ids];
  }

  clear(): void {
    this.parts.clear();
    this.version++;
  }

  toJSON(): BoardJSON {
    return {
      v: 1,
      cols: this.cols,
      rows: this.rows,
      elastic: this.elastic || undefined,
      ox: this.ox || undefined,
      oy: this.oy || undefined,
      parts: [...this.parts.values()].map((p) => ({ ...p, props: { ...p.props } })),
    };
  }

  static fromJSON(j: BoardJSON): Board {
    const b = new Board(j.cols, j.rows);
    b.elastic = !!j.elastic;
    b.ox = j.ox ?? 0;
    b.oy = j.oy ?? 0;
    for (const p of j.parts) b.insert(p);
    return b;
  }
}

export interface BoardJSON {
  v: 1;
  cols: number;
  rows: number;
  elastic?: boolean;
  ox?: number;
  oy?: number;
  parts: Placement[];
}

export { cellKey };
